/**
 * React wrapper for the 3D assembly view: the same imperative View3D shell
 * the room editor uses, created through the assembler's camera controls and
 * fed the composed level scene (every room built once by the editor's
 * pipeline, cached, and re-placed by transform). Walls are never culled here;
 * only the ceilings hide while looking down. Core's `AssemblyView3DScene`
 * decides what shows: the world holds still under the camera while rooms
 * move, the selected room wears its outline and floor wash as in 2D, and the
 * camera glides to center it once per selection. Where the view is editable
 * the selected room also wears the move gadget, whose drags land in the
 * assembler's gesture engine (beginRoomMove3D / moveRoom3D /
 * endAssemblerDrag): plan and floor elevation, snapped, committed, and undone
 * like a plan drag. Each room wears the 2D plan's own label, at its floor
 * center as the camera sees it (the selected room's at its middle, lifted
 * clear of the gadget where there is one); a click on a label selects its
 * room, and every other gesture over one reaches the view.
 */

import { css } from '@emotion/react';
import {
  type AssemblerSelection,
  AssemblyView3DScene,
  beginRoomMove3D,
  displayRooms,
  endAssemblerDrag,
  moveRoom3D,
  roomLabelAt,
  type SceneLabelFrame,
  sceneRoomLabels,
  useFloorPlanAssemblerState,
  useFloorPlanPreferences,
} from '@kaiizen/kai-floor-plan-editor-core';
import { useEffect, useRef, useState } from 'react';
import { useCreateView3D } from '../../view3d';
import { ViewCube } from '../ViewCube';
import { measuredLineWidth, RoomLabel } from './RoomLabel';

const ASSEMBLY_VIEW3D_CANVAS_STYLE = css`
  label: ASSEMBLY_VIEW3D_CANVAS_STYLE;
  position: absolute; inset: 0; width: 100%; height: 100%;
`;

const ASSEMBLY_VIEW3D_LABELS_STYLE = css`
  label: ASSEMBLY_VIEW3D_LABELS_STYLE;
  position: absolute; inset: 0; width: 100%; height: 100%;
  pointer-events: none; overflow: hidden;
`;

export function AssemblyView3DCanvas({ visible }: { visible: boolean }): React.JSX.Element {
  const floorPlanAssemblerState = useFloorPlanAssemblerState();
  const sceneRef = useRef(new AssemblyView3DScene());
  const stateRef = useRef(floorPlanAssemblerState);
  stateRef.current = floorPlanAssemblerState;
  const [labelFrame, setLabelFrame] = useState<SceneLabelFrame>({ labels: [], placements: [] });
  const { st } = floorPlanAssemblerState;

  const rooms = displayRooms(st);
  const view = useFloorPlanPreferences();
  const { layers } = view;
  const labels = sceneRoomLabels(labelFrame, st, view);
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  const { canvasRef, viewRef } = useCreateView3D<AssemblerSelection>(visible, {
    onSelect: selection => stateRef.current.select(selection ?? { kind: 'none' }),
    onGadget: {
      begin: () => {
        const { selection } = stateRef.current.st;
        if (selection.kind === 'room') stateRef.current.mutate(s => beginRoomMove3D(s, selection.id));
      },
      move: (delta, reach) => stateRef.current.mutate(s => moveRoom3D(s, delta, reach)),
      end: () => stateRef.current.mutate(s => endAssemblerDrag(s)),
    },
    onLabels: (sceneLabels, placements) => setLabelFrame({ labels: sceneLabels, placements }),
    pickOverlay: (x, y) => {
      const hit = roomLabelAt(labelsRef.current, x, y, { lineWidth: measuredLineWidth });
      return hit && { kind: 'room', id: hit.assemblyRoom.id };
    },
  });

  useEffect(() => {
    const view3d = viewRef.current;
    const frame = sceneRef.current.update(stateRef.current.st, { visible: visible && !!view3d, layers });
    if (!view3d) return;
    if (!frame) {
      view3d.hide();
      return;
    }
    view3d.setScene(frame.scene, frame.reframe || undefined);
    view3d.show();
    if (frame.centerOn) view3d.centerOn(frame.centerOn);
  }, [visible, rooms, st.level, st.readOnly, st.selection, layers, viewRef]);

  return (
    <>
      <canvas ref={canvasRef} css={ASSEMBLY_VIEW3D_CANVAS_STYLE} style={{ display: visible ? 'block' : 'none' }} data-testid='assembly-view3d' />
      <svg css={ASSEMBLY_VIEW3D_LABELS_STYLE} style={{ display: visible ? 'block' : 'none' }} data-testid='assembly-view3d-labels'>
        {labels.map(label => <RoomLabel key={label.assemblyRoom.id} {...label} />)}
      </svg>
      {visible && <ViewCube />}
    </>
  );
}
